import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import VideocamIcon from '@mui/icons-material/Videocam';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { errorMessage } from '@/api/common';
import { mediaNetworkApi } from '@/api/media-network/mediaNetworkApi';
import { mediaNetworkKeys } from '@/api/media-network/mediaNetworkQueries';
import { useUnsavedChangesPrompt } from '@/hooks/useUnsavedChangesPrompt';
import { MediaType, type Creative } from '@/models/media-network/Creative';
import type { Playlist, PlaylistItem } from '@/models/media-network/Playlist';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';
import { useToast } from '../common/toast/ToastProvider';
import PlaylistPreview from './dialogs/PlaylistPreview';

interface Props {
    playlist: Playlist;
    brandId: string;
    canUpdate: boolean;
    canDelete: boolean;
}

// A playlist's rotation: an ordered list of creatives picked from the library.
//
// There is NO UPLOAD HERE any more. Media goes into the library once and is arranged here, which is what
// makes a creative reusable across playlists and across features. The old version owned the whole upload
// handshake and treated the file as if it belonged to the playlist.
const PlaylistItemGrid = ({ playlist, brandId, canUpdate, canDelete }: Props) => {
    const strings = useLocalizedStrings();
    const toast = useToast();
    const qc = useQueryClient();

    const [picking, setPicking] = useState(false);
    const [previewing, setPreviewing] = useState(false);
    // Shapes the last-added creative could not fill. A warning, never a blocker.
    const [missing, setMissing] = useState<string[]>([]);
    // Non-null = reorder mode, holding the pending order as a list of item ids.
    const [draftOrder, setDraftOrder] = useState<string[] | null>(null);

    const items = [...playlist.items].sort((a, b) => a.displayOrder - b.displayOrder);

    const reordering = draftOrder !== null;
    const rows = reordering
        ? draftOrder.map((id) => items.find((i) => i.id === id)).filter((i): i is PlaylistItem => !!i)
        : items;

    // Dirty only once the pending order actually differs — entering the mode and leaving immediately
    // shouldn't prompt on the way out.
    const orderChanged = reordering && draftOrder.some((id, index) => items[index]?.id !== id);
    useUnsavedChangesPrompt(orderChanged);

    // Switching playlist abandons a half-finished reorder rather than carrying a draft of one playlist's ids
    // onto another's rows.
    useEffect(() => {
        setDraftOrder(null);
    }, [playlist.id]);

    const refresh = async () => {
        await qc.invalidateQueries({ queryKey: mediaNetworkKeys.list(brandId) });
        await qc.invalidateQueries({ queryKey: mediaNetworkKeys.detail(playlist.id) });
        // A creative's usage count moves when it joins or leaves a playlist.
        await qc.invalidateQueries({ queryKey: mediaNetworkKeys.creatives(brandId) });
    };

    const { mutateAsync: addItem, isPending: isAdding } = useMutation({
        mutationFn: (creativeId: string) => mediaNetworkApi.addItem(playlist.id, creativeId),
        onSuccess: async (result) => {
            setPicking(false);
            // NOT an error: the creative was added and is simply skipped on the surfaces it cannot fill.
            // Refusing would trade a small authoring gap for a blank screen.
            setMissing(result.missingProfiles.map((p) => `${p.name} (${p.display})`));
            await refresh();
        },
        onError: (e: unknown) => toast({
            severity: 'error',
            message: errorMessage(e, strings.playlistItemAddError),
        }),
    });

    const { mutateAsync: removeItem } = useMutation({
        mutationFn: (itemId: string) => mediaNetworkApi.removeItem(playlist.id, itemId),
        onSuccess: refresh,
        onError: () => toast({ severity: 'error', message: strings.playlistItemRemoveError }),
    });

    // Reorder sends the WHOLE list every time — the backend rejects a partial one rather than guessing where
    // the omitted items belong.
    //
    // Applied OPTIMISTICALLY to the list query, which is what actually feeds this grid: the rollback below
    // keeps it honest if the write fails, and invalidating alone would leave the rows sitting still until the
    // refetch lands.
    const { mutateAsync: reorder, isPending: isReordering } = useMutation({
        mutationFn: (orderedIds: string[]) => mediaNetworkApi.reorderItems(playlist.id, orderedIds),
        onMutate: async (orderedIds: string[]) => {
            const key = mediaNetworkKeys.list(brandId);
            // Stop an in-flight refetch from landing on top of the optimistic value.
            await qc.cancelQueries({ queryKey: key });
            const previous = qc.getQueryData<Playlist[]>(key);

            qc.setQueryData<Playlist[]>(key, (old) => old?.map((p) => p.id !== playlist.id ? p : {
                ...p,
                items: p.items.map((i) => {
                    const index = orderedIds.indexOf(i.id);
                    return index < 0 ? i : { ...i, displayOrder: index };
                }),
            }));

            return { previous, key };
        },
        onSuccess: () => toast({ severity: 'success', message: strings.playlistReorderSaved }),
        onError: (_e, _vars, context) => {
            if (context?.previous) qc.setQueryData(context.key, context.previous);
            toast({ severity: 'error', message: strings.playlistReorderError });
        },
        // Reconcile with whatever the server actually stored, success or failure.
        onSettled: async () => {
            await qc.invalidateQueries({ queryKey: mediaNetworkKeys.list(brandId) });
        },
    });

    // Moves rows in LOCAL state only; nothing is persisted until Save. One PUT for a session of rearranging
    // rather than one per click — dragging an item from tenth place to first is otherwise nine writes, and
    // nine round trips the user waits through one at a time.
    const move = (index: number, delta: number) => {
        const target = index + delta;
        if (target < 0 || target >= rows.length) return;
        const next = rows.map((i) => i.id);
        [next[index], next[target]] = [next[target], next[index]];
        setDraftOrder(next);
    };

    const saveOrder = async () => {
        if (!draftOrder) return;
        await reorder(draftOrder);
        setDraftOrder(null);
    };

    return (
        <Stack spacing={1}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle2">{strings.playlistItems}</Typography>
                {/* Reorder is a MODE, so its controls replace the normal ones rather than sitting beside
                    them — there is no adding to a rotation half way through rearranging it. */}
                {reordering ? (
                    <Stack direction="row" spacing={1}>
                        <Button onClick={() => setDraftOrder(null)} disabled={isReordering} size="input">
                            {strings.cancel}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => void saveOrder()}
                            loading={isReordering}
                            disabled={!orderChanged || isReordering}
                            size="input"
                        >
                            {strings.save}
                        </Button>
                    </Stack>
                ) : (
                    <Stack direction="row" spacing={1}>
                        {/* Not gated on canUpdate — reading what a screen will show is not editing it, and the
                            people who most need to check are often the ones who cannot change it. */}
                        <Button
                            variant="outlined"
                            size="input"
                            startIcon={<PlayArrowIcon />}
                            disabled={items.length === 0}
                            onClick={() => setPreviewing(true)}
                        >
                            {strings.playlistPreview}
                        </Button>
                        {canUpdate && (
                            <>
                                <Button
                                    size="input"
                                    startIcon={<SwapVertIcon />}
                                    // Nothing to rearrange with fewer than two.
                                    disabled={items.length < 2}
                                    onClick={() => setDraftOrder(items.map((i) => i.id))}
                                >
                                    {strings.playlistChangeOrder}
                                </Button>
                                <Button
                                    variant="contained"
                                    size="input"
                                    startIcon={<AddIcon />}
                                    onClick={() => { setMissing([]); setPicking(true); }}
                                >
                                    {strings.playlistAddFromLibrary}
                                </Button>
                            </>
                        )}
                    </Stack>
                )}
            </Stack>

            {reordering && (
                <Alert severity="info" variant="outlined">{strings.playlistReorderHint}</Alert>
            )}

            {missing.length > 0 && !reordering && (
                <Alert severity="warning" variant="outlined" onClose={() => setMissing([])}>
                    {strings.playlistItemMissingShapes.replace('{{shapes}}', missing.join(', '))}
                </Alert>
            )}

            {items.length === 0 && (
                <Typography variant="body2" color="text.secondary">{strings.playlistNoItems}</Typography>
            )}

            {rows.map((item, index) => (
                <ItemRow
                    key={item.id}
                    item={item}
                    index={index}
                    total={rows.length}
                    reordering={reordering}
                    canDelete={canDelete}
                    onMove={move}
                    onRemove={() => removeItem(item.id)}
                    strings={strings}
                />
            ))}

            <LibraryPicker
                open={picking}
                brandId={brandId}
                busy={isAdding}
                // Already in the rotation — the API rejects a duplicate, so filter rather than let the user
                // pick something that will error.
                excludeIds={items.map((i) => i.creative.id)}
                onPick={(creativeId) => addItem(creativeId)}
                onClose={() => setPicking(false)}
                strings={strings}
            />

            {/* Unmounted while closed so the loop, its timers and its video stop dead on close rather than
                playing on behind the dialog. */}
            {previewing && (
                <PlaylistPreview
                    open
                    playlist={playlist}
                    brandId={brandId}
                    onClose={() => setPreviewing(false)}
                />
            )}
        </Stack>
    );
};

const ItemRow = ({
    item, index, total, reordering, canDelete, onMove, onRemove, strings,
}: {
    item: PlaylistItem;
    index: number;
    total: number;
    reordering: boolean;
    canDelete: boolean;
    onMove: (index: number, delta: number) => void;
    onRemove: () => void;
    strings: ReturnType<typeof useLocalizedStrings>;
}) => {
    const hasVideo = item.creative.renditions.some((r) => r.mediaType === MediaType.Video);

    return (
        <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 24 }}>{index + 1}</Typography>

                {hasVideo ? <VideocamIcon fontSize="small" color="action" /> : <ImageIcon fontSize="small" color="action" />}

                <Stack sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap>{item.creative.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {strings.renditionCount.replace('{{count}}', String(item.creative.renditions.length))}
                    </Typography>
                </Stack>

                {item.creative.advertiserAssetCode && (
                    <Chip size="small" variant="outlined" label={item.creative.advertiserAssetCode} />
                )}

                {/* Reorder mode shows ONLY the arrows. Removing stays out of it so a row can't be pulled out
                    from under a pending order, which would leave the draft holding an id that no longer
                    exists. */}
                {reordering ? (
                    <>
                        <IconButton size="small" disabled={index === 0} onClick={() => onMove(index, -1)}
                            aria-label="move up">
                            <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" disabled={index === total - 1} onClick={() => onMove(index, 1)}
                            aria-label="move down">
                            <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                    </>
                ) : canDelete && (
                    <IconButton size="small" color="error" onClick={onRemove} aria-label="remove item">
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                )}
            </Stack>
        </Paper>
    );
};

// Picks an existing creative. Upload lives in the library, not here — one way for media to enter the system.
const LibraryPicker = ({
    open, brandId, busy, excludeIds, onPick, onClose, strings,
}: {
    open: boolean;
    brandId: string;
    busy: boolean;
    excludeIds: string[];
    onPick: (creativeId: string) => void;
    onClose: () => void;
    strings: ReturnType<typeof useLocalizedStrings>;
}) => {
    const { data: creatives = [], isPending } = useQuery({
        queryKey: mediaNetworkKeys.creatives(brandId),
        enabled: open,
        queryFn: () => mediaNetworkApi.listCreatives(brandId),
    });

    const available = creatives.filter((c: Creative) => !excludeIds.includes(c.id) && c.renditions.length > 0);

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{strings.playlistAddFromLibrary}</DialogTitle>
            <DialogContent dividers>
                {/* A creative with no files cannot play, so it is not offered — adding one would put a row in
                    the rotation that resolves to nothing. */}
                {!isPending && available.length === 0 && (
                    <Typography variant="body2" color="text.secondary">{strings.playlistLibraryEmpty}</Typography>
                )}
                <List dense>
                    {available.map((c) => (
                        <ListItemButton key={c.id} disabled={busy} onClick={() => onPick(c.id)}>
                            <ListItemText
                                primary={c.name}
                                secondary={strings.renditionCount.replace('{{count}}', String(c.renditions.length))}
                            />
                        </ListItemButton>
                    ))}
                </List>
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={onClose} disabled={busy}>{strings.cancel}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default PlaylistItemGrid;
